import { useEffect, useRef } from "react";

/**
 * Calls the provided callback whenever a navigation event occurs (Excel ribbon
 * nav, sidebar clicks, programmatic navigation). Use to dismiss any open
 * dialogs/popups when the user changes pages — handles the same-route case
 * where React Router's unmount doesn't fire (e.g. clicking "Go to Discussions"
 * while already on /discussions).
 *
 * Subscribes to the global `closeAllPopups` window event, which `MainLayout`
 * dispatches on every navigation. The callback is ref-pinned so the listener
 * isn't re-bound on every render if the parent's setters change identity.
 */
export function useCloseOnNavigation(close: () => void): void {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    // Skip closeAllPopups events fired within one tick of mount. When a page
    // arrives via a route change, MainLayout dispatches closeAllPopups from
    // its own pathname-change effect AFTER child effects have run — fine
    // for pages that were already there, but wrongly closes popups the
    // arriving page just opened in its own mount effect (e.g. Discussions'
    // ?create=true auto-open flow, triggered by the ribbon's Create Review
    // Point button). Real subsequent navigations happen well after this tick.
    let ready = false;
    const timer = setTimeout(() => { ready = true; }, 0);
    const handler = () => { if (ready) closeRef.current(); };
    window.addEventListener("closeAllPopups", handler);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("closeAllPopups", handler);
    };
  }, []);
}
